import React from "react";
import { Button } from "@progress/kendo-react-buttons";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { Field, FieldArrayRenderProps } from "@progress/kendo-react-form";
import {
  CustomAppDialog,
  CustomDialogCloseButton,
} from "../../components/dialog/Dialog";
import { Typography } from "@progress/kendo-react-common";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { MdDelete } from "react-icons/md";
import IconButton from "../../components/common/IconButton";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { IItem } from "../item/itemModel";
import FormNumericTextField from "../../components/formFields/FormNumericTextField";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import {
  closePartAddtoOrdersRawItemDeleteConfirmDialog,
  openPartAddtoOrdersRawItemDeleteConfirmDialog,
  setPartAddtoOrdersRawItemIndex,
} from "./partAddtoOrdersSlice";
import { findAllActiveItem } from "../item/services/item.services";

const PartAddtoOrdersRawItemArray = (
  fieldArrayRenderProps: FieldArrayRenderProps,
) => {
  const dispatch = useAppDispatch();
  const PartAddtoOrdersRawItemIndex = useAppSelector(
    (state) => state.partAddtoOrders.PartAddtoOrdersRawItemIndex,
  );
  const dialogName = useAppSelector((state) => state.dialog.dialogName);
  const isViewDialog = dialogName.startsWith("view-raw-items-");
  const PartAddtoOrdersRawItemDeleteConfirmDialog = useAppSelector(
    (state) => state.partAddtoOrders.PartAddtoOrdersRawItemDeleteConfirmDialog,
  );
  const fieldName = fieldArrayRenderProps.name;
  const ItemList = useAppSelector((state) => state.item.ItemList);

  const calculateTotal = (grossWeight: number, rate: number) => {
    return (grossWeight || 0) * (rate || 0);
  };

  const handleDeleteAction = React.useCallback(
    async (index: any) => {
      if (fieldArrayRenderProps?.value[index]?.ID) {
        // API delete call if needed
        fieldArrayRenderProps.onRemove({ index: index });
        dispatch(closePartAddtoOrdersRawItemDeleteConfirmDialog());
        dispatch(setPartAddtoOrdersRawItemIndex(-1));
      } else {
        fieldArrayRenderProps.onRemove({ index: index });
        dispatch(closePartAddtoOrdersRawItemDeleteConfirmDialog());
        dispatch(setPartAddtoOrdersRawItemIndex(-1));
      }
    },
    [fieldArrayRenderProps, dispatch],
  );

  const pushElementInarray = React.useCallback(() => {
    fieldArrayRenderProps?.onPush({
      value: {
        item_id: null,
        GrossWeight: null,
        NetWeight: null,
        Rate: null,
        Total: null,
      },
    });
  }, [fieldArrayRenderProps]);

  const handleOpenDeleteDialog = (index: any) => {
    dispatch(openPartAddtoOrdersRawItemDeleteConfirmDialog());
    dispatch(setPartAddtoOrdersRawItemIndex(index));
  };

  return (
    <>
      <CustomAppDialog open={PartAddtoOrdersRawItemDeleteConfirmDialog}>
        <>
          <Typography.h5>{"Delete Raw Item"}</Typography.h5>
          <GridLayout>
            <GridLayoutItem>
              <Typography.p>
                Are you sure you want to delete this raw item?
              </Typography.p>
            </GridLayoutItem>
            <GridLayoutItem
              style={{
                display: "flex",
                justifyContent: "end",
              }}
            >
              <CustomDialogCloseButton
                themeColor="error"
                closeDialogFunction={
                  closePartAddtoOrdersRawItemDeleteConfirmDialog
                }
              />
              <Button
                fillMode={"solid"}
                themeColor={"error"}
                onClick={() => handleDeleteAction(PartAddtoOrdersRawItemIndex)}
                type="button"
              >
                Delete
              </Button>
            </GridLayoutItem>
          </GridLayout>
        </>
      </CustomAppDialog>

      <GridLayout cols={[{ width: "50%" }, { width: "50%" }]}>
        <GridLayoutItem>
          <Typography.h5 style={{ marginBottom: 5 }}>
            Raw Items Detail
          </Typography.h5>
        </GridLayoutItem>
        <GridLayoutItem style={{ textAlign: "left", marginLeft: "auto" }}>
          {!isViewDialog && (
            <Button
              fillMode={"solid"}
              themeColor={"primary"}
              onClick={pushElementInarray}
              type="button"
            >
              Add Raw Item
            </Button>
          )}
        </GridLayoutItem>
      </GridLayout>

      {fieldArrayRenderProps.value &&
        fieldArrayRenderProps.value.length > 0 && (
          <div
            className="table-responsive"
            style={{
              maxHeight: isViewDialog ? "50vh" : "30vh",
              scrollbarWidth: "thin",
              marginTop: 10,
            }}
          >
            <table
              className="table table-striped table-bordered table-hover field-array-table"
              style={{ minWidth: "100%" }}
            >
              <thead>
                <tr>
                  <th style={{ width: "25%" }}>Product Name</th>
                  <th style={{ width: "15%" }}>Gross Weight (gm)</th>
                  <th style={{ width: "15%" }}>Net Weight (gm)</th>
                  <th style={{ width: "15%" }}>Rate (₹)</th>
                  <th style={{ width: "15%" }}>Total (₹)</th>
                  <th style={{ width: "5%", position: "sticky" }}></th>
                </tr>
              </thead>
              <tbody>
                {fieldArrayRenderProps.value &&
                  fieldArrayRenderProps.value.length > 0 &&
                  fieldArrayRenderProps.value.map(
                    (rawItem: any, index: number) => (
                      <tr key={index}>
                        <td>
                          <Field
                            wrapperStyle={{ margin: 0 }}
                            name={`${fieldName}.${index}.item_id`}
                            placeholder="Select Item"
                            isAddNew={true}
                            addNewLink="item/create"
                            dispatcherFunction={findAllActiveItem}
                            component={FormSelectionField}
                            validator={requiredValidator}
                            disabled={isViewDialog}
                            options={ItemList?.map((item: IItem) => {
                              return {
                                value: item.id,
                                label: item.item_name,
                              };
                            })}
                            onChange={(e: any) => {
                              // Optional: Fetch item details when selected
                              if (e?.value) {
                                // You can add logic to fetch item details here
                              }
                            }}
                          />
                        </td>
                        <td>
                          <Field
                            wrapperStyle={{ margin: 0 }}
                            name={`${fieldName}.${index}.GrossWeight`}
                            placeholder="0.000"
                            type="number"
                            format="n3"
                            component={FormNumericTextField}
                            validator={requiredValidator}
                            disabled={isViewDialog}
                            onChange={(e: any) => {
                              const grossWeight = e.value;
                              const rate =
                                fieldArrayRenderProps.formRenderProps.valueGetter(
                                  `${fieldName}.${index}.Rate`,
                                );
                              const total = calculateTotal(grossWeight, rate);
                              fieldArrayRenderProps.formRenderProps.onChange(
                                `${fieldName}.${index}.Total`,
                                { value: total },
                              );
                            }}
                          />
                        </td>
                        <td>
                          <Field
                            wrapperStyle={{ margin: 0 }}
                            name={`${fieldName}.${index}.NetWeight`}
                            placeholder="0.000"
                            type="number"
                            format="n3"
                            component={FormNumericTextField}
                            validator={requiredValidator}
                            disabled={isViewDialog}
                          />
                        </td>
                        <td>
                          <Field
                            wrapperStyle={{ margin: 0 }}
                            name={`${fieldName}.${index}.Rate`}
                            placeholder="0.00"
                            type="number"
                            format="n2"
                            component={FormNumericTextField}
                            disabled={isViewDialog}
                            onChange={(e: any) => {
                              const rate = e.value;
                              const grossWeight =
                                fieldArrayRenderProps.formRenderProps.valueGetter(
                                  `${fieldName}.${index}.GrossWeight`,
                                );
                              const total = calculateTotal(grossWeight, rate);
                              fieldArrayRenderProps.formRenderProps.onChange(
                                `${fieldName}.${index}.Total`,
                                { value: total },
                              );
                            }}
                          />
                        </td>
                        <td>
                          <Field
                            wrapperStyle={{ margin: 0 }}
                            name={`${fieldName}.${index}.Total`}
                            placeholder="0.00"
                            type="number"
                            format="n2"
                            component={FormNumericTextField}
                            disabled={true}
                          />
                        </td>
                        <td
                          style={{
                            position: "sticky",
                            right: 0,
                            zIndex: 2,
                          }}
                        >
                          <IconButton
                            type="button"
                            fillMode="solid"
                            size="small"
                            title="Delete"
                            themeColor="error"
                            style={{
                              position: "absolute",
                              height: 32,
                              width: 32,
                              right: 15,
                              pointerEvents: "all",
                            }}
                            onClick={() => handleOpenDeleteDialog(index)}
                          >
                            <MdDelete
                              className="absolute-position"
                              style={{ fontSize: "22px" }}
                            />
                          </IconButton>
                        </td>
                      </tr>
                    ),
                  )}
              </tbody>
            </table>
          </div>
        )}
    </>
  );
};

export default PartAddtoOrdersRawItemArray;
